import { Ornament } from "@/components/ui/Ornament";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import type { DonationConfig, Event } from "@/lib/event-schema";
import { Heart, Loader2, Lock, Mail, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type CardConfig = {
  title: string;
  description?: string;
  suggestedAmounts: number[];
  allowCustom: boolean;
  stripeReady?: boolean;
  /** Per-event campaigns may show a goal; the site-wide fund does not. */
  goalCents?: number;
  raisedCents?: number;
};

type CardProps = {
  config: CardConfig;
  /** Unique prefix for aria/id wiring so the card can mount more than once on a page. */
  idPrefix: string;
  /** Used as Stripe metadata once keys land. */
  surface: "event" | "site";
  eventSlug?: string;
  campaignId?: string;
  /** Override the default heading label. */
  eyebrow?: string;
};

// Visual + interaction primitive shared between the per-event donation
// module and the site-wide /donate page. Keeps the donation widget in
// one place so the two surfaces never drift visually.
export function DonationCard({
  config,
  idPrefix,
  surface,
  eventSlug,
  campaignId,
  eyebrow = surface === "event" ? "Support this event" : "Support the Mobeen Center",
}: CardProps) {
  const [picked, setPicked] = useState<number | "custom">(config.suggestedAmounts[1] ?? 50);
  const [custom, setCustom] = useState("");
  const [pending, setPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // Live totalCents from the public totals endpoint. Starts undefined so
  // we render the static raisedCents from props until the first fetch
  // lands; once we have a server value we trust it and re-fetch on
  // success-return so the new donation appears.
  const [liveCents, setLiveCents] = useState<number | undefined>(undefined);
  // Brief one-shot highlight when liveCents *changes* after the initial
  // fetch (i.e. a donation just landed during the user's visit). The
  // ref guards against firing on the first arrival.
  const [bump, setBump] = useState(false);
  const lastLiveRef = useRef<number | undefined>(undefined);
  // Per-attempt idempotency key, stable across retries inside a single
  // mount. Regenerated only on explicit user retry after an error so
  // network-level retries reuse the same Stripe Checkout Session
  // instead of creating a duplicate.
  const clientReferenceRef = useRef<string>("");
  if (clientReferenceRef.current === "") {
    clientReferenceRef.current =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  // True while the URL carries ?donation=success and we haven't yet
  // observed liveCents change. Lets us show an honest "donation may
  // take a moment to appear" hint instead of a stale $0.
  const [waitingForTotal, setWaitingForTotal] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("donation") === "success";
  });
  const ready = Boolean(config.stripeReady);

  const effectiveRaisedCents = liveCents ?? config.raisedCents ?? 0;
  const goal = config.goalCents ? config.goalCents / 100 : null;
  const raised = effectiveRaisedCents / 100;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const showRaised = effectiveRaisedCents > 0 || goal !== null;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data =
          surface === "site"
            ? await api.donate.totals.site()
            : eventSlug && campaignId
              ? await api.donate.totals.event(eventSlug, campaignId)
              : null;
        if (!cancelled && data) setLiveCents(data.totalCents);
      } catch (err) {
        // Totals are advisory; fall back to props on error. Log so an
        // operator sees the failure if it persists.
        console.warn("donate:totals fetch failed", err);
      }
    }
    void load();
    // Re-fetch when the URL signals a successful return from Checkout
    // (the success_url stamps ?donation=success). KV is eventually
    // consistent, so the new donation may take a few seconds; one
    // delayed retry covers that window without polling indefinitely.
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("donation") === "success"
    ) {
      const t = window.setTimeout(load, 3000);
      // Stop showing the hint after the delayed retry has had a chance,
      // even if liveCents didn't move (donor saw correct number).
      const tHint = window.setTimeout(() => setWaitingForTotal(false), 5000);
      return () => {
        cancelled = true;
        window.clearTimeout(t);
        window.clearTimeout(tHint);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [surface, eventSlug, campaignId]);

  useEffect(() => {
    if (liveCents === undefined) return;
    // First fetch: record but don't bump. Subsequent changes pulse and
    // clear the post-donation hint.
    if (lastLiveRef.current === undefined) {
      lastLiveRef.current = liveCents;
      return;
    }
    if (liveCents === lastLiveRef.current) return;
    lastLiveRef.current = liveCents;
    setBump(true);
    setWaitingForTotal(false);
    const t = window.setTimeout(() => setBump(false), 900);
    return () => window.clearTimeout(t);
  }, [liveCents]);

  const amount = picked === "custom" ? Number(custom) || 0 : picked;
  // Posts to /api/donate/checkout via the typed api helper. The donate
  // button is gated by `ready`, so an unconfigured Stripe surface never
  // reaches the server.
  async function startCheckout(): Promise<void> {
    if (!ready || amount <= 0 || pending) return;
    setCheckoutError(null);
    setPending(true);
    try {
      const data = await api.donate.checkout({
        surface,
        eventSlug,
        campaignId,
        amountCents: Math.round(amount * 100),
        clientReference: clientReferenceRef.current,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // Reached only if the server returned 2xx with no redirect URL.
      // Stripe Checkout always returns a URL; surface a generic error.
      setCheckoutError("Something went wrong. Please try again.");
    } catch (err) {
      console.error("donate:checkout failed", err);
      setCheckoutError("We couldn't start checkout. Please try again or contact the Center.");
    } finally {
      setPending(false);
    }
  }

  // Rotate the clientReference on explicit user retry so the next
  // attempt creates a fresh Stripe Checkout Session. Network-level
  // retries (silent fetch retries by the client/browser) still reuse
  // the existing key, which is the point of idempotency.
  function dismissErrorAndRotate(): void {
    clientReferenceRef.current =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setCheckoutError(null);
  }

  return (
    <section
      id="donate"
      className="relative mx-auto w-full max-w-4xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${idPrefix}-donate`}
    >
      <div className="surface-card relative overflow-hidden p-6 sm:p-8 lg:p-10">
        <Ornament className="mb-8" />

        <div className="relative grid gap-10 lg:grid-cols-[1fr,1.1fr] lg:items-center">
          <div>
            <p className="eyebrow text-accent inline-flex items-center gap-2">
              <Heart className="h-3.5 w-3.5" strokeWidth={1.75} />
              {eyebrow}
            </p>
            <h2
              id={`${idPrefix}-donate`}
              className="display-title mt-3 text-[clamp(1.85rem,4vw,2.85rem)] leading-tight"
            >
              {config.title}
            </h2>
            {config.description ? (
              <p className="mt-3 text-pretty text-muted leading-relaxed">{config.description}</p>
            ) : null}

            {showRaised ? (
              <div className="mt-7">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className={cn(
                      "font-display font-light text-ink leading-none tracking-title",
                      "text-[clamp(2.25rem,5.5vw,3.6rem)]",
                      bump && "animate-rise",
                    )}
                  >
                    ${raised.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted">
                    {goal ? (
                      <>
                        raised of <span className="text-ink">${goal.toLocaleString()}</span>
                      </>
                    ) : (
                      "raised so far"
                    )}
                  </span>
                </div>
                {goal ? (
                  <>
                    {/* biome-ignore lint/a11y/useFocusableInteractive: progressbar is a display widget, not a keyboard-interactive control */}
                    <div
                      className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-line/60"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={pct}
                      aria-label={`${pct}% of fundraising goal`}
                    >
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-accent/70 to-accent transition-[width] duration-700 ease-out"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="mt-1.5 text-[11px] uppercase tracking-[0.18em] text-subtle">
                      {pct}% of goal
                    </p>
                  </>
                ) : null}
                {waitingForTotal ? (
                  <p className="mt-3 text-xs text-muted">
                    Thank you. Your donation may take a moment to appear above.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="grid gap-4">
            <AmountGrid
              amounts={config.suggestedAmounts}
              allowCustom={config.allowCustom}
              picked={picked}
              custom={custom}
              onPick={setPicked}
              onCustom={setCustom}
            />

            <button
              type="button"
              onClick={() => void startCheckout()}
              disabled={!ready || amount <= 0 || pending}
              title={ready ? undefined : "Stripe checkout will be enabled soon."}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium transition-all focus-ring",
                ready
                  ? "bg-ink text-canvas hover:bg-ink/90 dark:bg-accent dark:text-accent-on shadow-glow"
                  : "bg-ink/40 text-canvas cursor-not-allowed",
                pending && "opacity-70 cursor-wait",
              )}
            >
              {pending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Lock className="h-4 w-4" strokeWidth={1.75} />
              )}
              {!ready
                ? "Checkout coming soon"
                : amount <= 0
                  ? "Choose an amount"
                  : pending
                    ? "Starting checkout"
                    : `Give $${amount}`}
            </button>
            {checkoutError ? (
              <p role="alert" className="text-center text-xs text-red-600 dark:text-red-400">
                {checkoutError}{" "}
                <button
                  type="button"
                  onClick={dismissErrorAndRotate}
                  className="underline underline-offset-4 hover:text-red-700 dark:hover:text-red-300 focus-ring rounded-sm"
                >
                  Dismiss
                </button>
              </p>
            ) : null}
            <p className="text-center text-[11px] text-subtle">
              Payments processed by Stripe. We never see your card details.
            </p>
          </div>
        </div>

        <ZelleFootnote />
      </div>
    </section>
  );
}

function ZelleFootnote() {
  return (
    <div className="mt-10 border-t border-line/60 pt-6">
      <div className="flex flex-col items-center text-center">
        <p className="eyebrow text-accent">Prefer Zelle?</p>
        <p className="mt-2 max-w-md text-sm text-muted leading-relaxed">
          Send directly to the Mobeen Center, no fee. Use the email or phone number below in your
          Zelle app.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
          <a
            href="mailto:mobeencenter@gmail.com"
            className="inline-flex items-center gap-1.5 text-ink hover:text-accent focus-ring rounded-sm"
          >
            <Mail className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} />
            mobeencenter@gmail.com
          </a>
          <a
            href="tel:+18583061550"
            className="inline-flex items-center gap-1.5 text-ink hover:text-accent focus-ring rounded-sm"
          >
            <Phone className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} />
            (858) 306-1550
          </a>
        </div>
        <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-subtle">
          Recipient: Mobeen <span aria-hidden>·</span>{" "}
          <span className="text-emerald-600 dark:text-emerald-400">No fee</span>
        </p>
      </div>
    </div>
  );
}

// Unified amount picker: preset tiles and (optionally) an "Other" cell
// that swaps between a button (closed) and a label-wrapped input
// (active). Both modes render in the same grid slot with the same
// styling so the visual rhythm is preserved; using a label rather than
// nesting an input inside a button keeps the markup valid.
function AmountGrid({
  amounts,
  allowCustom,
  picked,
  custom,
  onPick,
  onCustom,
}: {
  amounts: number[];
  allowCustom: boolean;
  picked: number | "custom";
  custom: string;
  onPick: (next: number | "custom") => void;
  onCustom: (value: string) => void;
}) {
  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(86px, 1fr))" }}
    >
      {amounts.map((amt) => (
        <button
          key={amt}
          type="button"
          onClick={() => onPick(amt)}
          className={cn(
            "rounded-2xl border px-3 py-4 text-center transition-all focus-ring",
            picked === amt
              ? "border-accent bg-accent text-accent-on shadow-glow"
              : "border-line/70 bg-surface hover:border-accent/60",
          )}
        >
          <span className="block font-display text-xl font-light">${amt}</span>
        </button>
      ))}
      {allowCustom ? (
        picked === "custom" ? (
          <label
            className="cursor-text rounded-2xl border border-accent bg-surface-raised px-3 py-4 text-center transition-all focus-within:ring-2 focus-within:ring-accent/30"
            aria-label="Custom donation amount"
          >
            <span className="inline-flex items-center justify-center gap-0.5 text-ink">
              <span className="text-sm text-muted">$</span>
              <input
                type="number"
                min={1}
                placeholder="0"
                value={custom}
                onChange={(e) => onCustom(e.target.value)}
                // biome-ignore lint/a11y/noAutofocus: focus expected when the user explicitly switches to the custom tile
                autoFocus
                className="w-14 bg-transparent text-right font-display text-xl font-light outline-none"
              />
            </span>
          </label>
        ) : (
          <button
            type="button"
            onClick={() => onPick("custom")}
            className="rounded-2xl border border-line/70 bg-surface px-3 py-4 text-center transition-all hover:border-accent/60 focus-ring"
            aria-label="Enter a custom amount"
          >
            <span className="block font-display text-xl font-light text-muted">Other</span>
          </button>
        )
      ) : null}
    </div>
  );
}

// Adapter for the per-event module registry. Forwards the event-scoped
// config into the shared DonationCard with the right metadata.
type ModuleProps = {
  config: DonationConfig;
  event: Event;
};

export function DonationModule({ config, event }: ModuleProps) {
  return (
    <DonationCard
      config={config}
      idPrefix={event.slug}
      surface="event"
      eventSlug={event.slug}
      campaignId={event.slug}
      eyebrow="Support this event"
    />
  );
}
